import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'

// Criar instância do axios
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Interceptor de requisição
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    const url = config.url || ''
    const isDriverRequest = url.includes('/delivery/drivers/login') || 
                            url.includes('/orders/driver/') ||
                            url.includes('/driver-deliver')
    
    // Rotas da área do entregador devem usar o driver_token
    if (isDriverRequest) {
      const driverToken = localStorage.getItem('driver_token')
      if (driverToken) {
        config.headers.Authorization = `Bearer ${driverToken}`
      }
    } else if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    } else {
      const driverToken = localStorage.getItem('driver_token')
      if (driverToken) {
        config.headers.Authorization = `Bearer ${driverToken}`
      }
    }
    
    // Adicionar company_id do localStorage se existir
    const companyId = localStorage.getItem('company_id')
    if (companyId && !config.params?.company_id) {
      if (!config.params) {
        config.params = {}
      }
      config.params.company_id = companyId
    }
    
    // Device ID de segurança do Mercado Pago (gerado pelo security.js no index.html)
    const mpDeviceId = (window as any).MP_DEVICE_SESSION_ID
    if (mpDeviceId) {
      config.headers['X-Device-Id'] = mpDeviceId
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Função para mostrar toast de erro (lazy import para evitar problemas de contexto)
const showErrorToast = async (message: string) => {
  try {
    const { useToast } = await import('vue-toastification')
    const toast = useToast()
    toast.error(message)
  } catch {
    // Silently fail if toast is not available
    console.error(message)
  }
}

// Interceptor de resposta
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const authStore = useAuthStore()
    
    if (error.response) {
      const status = error.response.status
      const data = error.response.data as any
      
      // Erro 401 - Não autorizado
      if (status === 401) {
        const requestUrl = error.config?.url || ''
        const isDriverRequest = requestUrl.includes('/delivery/drivers/login') || 
                                requestUrl.includes('/orders/driver/') ||
                                requestUrl.includes('/driver-deliver')
        const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh')
        
        if (isDriverRequest) {
          // Área do motorista: não faz logout de admin nem redireciona
          return Promise.reject(error)
        }
        
        authStore.logout()
        await showErrorToast('Sessão expirada. Faça login novamente.')
        if (!isAuthRequest) {
          // Redireciona superadmin para login de superadmin, admin comum para login normal
          router.push(authStore.isSuperAdmin ? '/superadmin/login' : '/login')
        }
        return Promise.reject(error)
      }
      
      // Erro 403 - Acesso negado
      if (status === 403) {
        await showErrorToast('Você não tem permissão para realizar esta ação.')
        return Promise.reject(error)
      }
      
      // Erro 404 - Não encontrado
      if (status === 404) {
        const config = error.config as any
        if (!config?._skipErrorToast) {
          await showErrorToast('Recurso não encontrado.')
        }
        return Promise.reject(error)
      }
      
      // Erro 422 - Validação
      if (status === 422) {
        const errors = data.errors
        if (errors) {
          Object.values(errors).forEach((errorList: any) => {
            errorList.forEach(async (err: string) => {
              await showErrorToast(err)
            })
          })
        } else {
          await showErrorToast(data.detail || 'Erro de validação.')
        }
        return Promise.reject(error)
      }
      
      // Erro 500 - Erro interno
      if (status >= 500) {
        await showErrorToast('Erro interno do servidor. Tente novamente mais tarde.')
        return Promise.reject(error)
      }
      
      // Outros erros
      await showErrorToast(data.detail || 'Ocorreu um erro. Tente novamente.')
    } else if (error.request) {
      // Erro de rede
      await showErrorToast('Erro de conexão. Verifique sua internet.')
    } else {
      await showErrorToast('Erro ao processar requisição.')
    }
    
    return Promise.reject(error)
  }
)

// APIs específicas
export const authApi = {
  login: (email: string, password: string, recaptchaToken?: string) =>
    api.post('/auth/login/json', { email, password, recaptcha_token: recaptchaToken }),
  
  me: () =>
    api.get('/auth/me'),
  
  refresh: () =>
    api.post('/auth/refresh'),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, new_password: string) =>
    api.post('/auth/reset-password', { token, new_password }),
}

export const userApi = {
  getAll: () =>
    api.get('/users'),
  
  get: (id: number) =>
    api.get(`/users/${id}`),
  
  create: (data: any) =>
    api.post('/users', data),
  
  update: (id: number, data: any) =>
    api.put(`/users/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/users/${id}`),
}

export const companyApi = {
  getBySlug: (slug: string) =>
    api.get(`/companies/public/${slug}`),
  
  getById: (id: number) =>
    api.get(`/companies/public/by-id/${id}`),
  
  getAdmin: (id: number) =>
    api.get(`/companies/${id}`),
  
  getBySubdomain: (subdomain: string) =>
    api.get(`/companies/public/by-subdomain/${subdomain}`),
  
  getByDomain: (domain: string) =>
    api.get(`/companies/public/by-domain/${domain}`),
  
  getByHostname: (hostname: string) =>
    api.get(`/companies/public/by-hostname/${hostname}`),
  
  getAll: () =>
    api.get('/companies'),
  
  create: (data: any) =>
    api.post('/companies', data),
  
  createWithAdmin: (data: any) =>
    api.post('/companies/with-admin', data),
  
  update: (id: number, data: any) =>
    api.put(`/companies/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/companies/${id}`),
  
  testMercadoPago: (id: number) =>
    api.post(`/companies/${id}/test-mercado-pago`),
}

export const categoryApi = {
  getByCompany: (companyId: number) =>
    api.get(`/categories/company/${companyId}`),
  
  getByCompanyAdmin: (companyId: number) =>
    api.get(`/categories/company/${companyId}/admin`),
  
  get: (id: number) =>
    api.get(`/categories/${id}`),
  
  create: (data: any) =>
    api.post('/categories', data),
  
  update: (id: number, data: any) =>
    api.put(`/categories/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/categories/${id}`),
  
  uploadImage: (id: number, formData: FormData) =>
    api.post(`/categories/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  deleteImage: (id: number) =>
    api.delete(`/categories/${id}/image`),
}

export const productApi = {
  getByCompany: (companyId: number, params?: any) =>
    api.get(`/products/company/${companyId}`, { params }),
  
  getByCompanyAdmin: (companyId: number) =>
    api.get(`/products/company/${companyId}/admin`),
  
  get: (id: number) =>
    api.get(`/products/${id}`),
  
  create: (data: any) =>
    api.post('/products', data),
  
  update: (id: number, data: any) =>
    api.put(`/products/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/products/${id}`),
  
  toggleAvailability: (id: number) =>
    api.patch(`/products/${id}/toggle-availability`),
  
  // Upload de imagens
  uploadImage: (productId: number, imageFile: File, isMain: boolean = false) => {
    const formData = new FormData()
    formData.append('image', imageFile)
    return api.post(`/products/${productId}/images`, formData, {
      params: { is_main: isMain },
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  
  deleteImage: (productId: number, imageUrl: string) =>
    api.delete(`/products/${productId}/images`, {
      params: { image_url: imageUrl }
    }),
  
  setMainImage: (productId: number, imageUrl: string) =>
    api.patch(`/products/${productId}/images/set-main`, null, {
      params: { image_url: imageUrl }
    }),
}

export const orderApi = {
  getByCompany: (companyId: number, params?: any) =>
    api.get(`/orders/company/${companyId}`, { params }),
  
  getStats: (companyId: number) =>
    api.get(`/orders/company/${companyId}/stats`),
  
  get: (id: number) =>
    api.get(`/orders/${id}`),
  
  getByNumber: (number: string, config?: any) =>
    api.get(`/orders/number/${number}`, config),
  
  create: (data: any) =>
    api.post('/orders', data),
  
  updateStatus: (id: number, data: any) =>
    api.patch(`/orders/${id}/status`, data),
  
  rate: (id: number, data: any) =>
    api.post(`/orders/${id}/rate`, data),
}

export const customerApi = {
  getByCompany: (companyId: number, params?: any) =>
    api.get(`/customers/company/${companyId}`, { params }),
  
  get: (id: number) =>
    api.get(`/customers/${id}`),
  
  getByPhone: (phone: string, companyId: number) =>
    api.get(`/customers/phone/${phone}`, { params: { company_id: companyId } }),
  
  create: (data: any) =>
    api.post('/customers', data),
  
  update: (id: number, data: any) =>
    api.put(`/customers/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/customers/${id}`),
}

export const driverApi = {
  getByCompany: (companyId: number, activeOnly?: boolean) =>
    api.get(`/delivery/drivers/company/${companyId}`, { params: { active_only: activeOnly } }),

  get: (id: number) =>
    api.get(`/delivery/drivers/${id}`),

  create: (data: any) =>
    api.post('/delivery/drivers', data),

  update: (id: number, data: any) =>
    api.put(`/delivery/drivers/${id}`, data),

  delete: (id: number) =>
    api.delete(`/delivery/drivers/${id}`),
}

export const driverAuthApi = {
  login: (password: string) =>
    api.post('/delivery/drivers/login', { password }),

  getMyOrders: () =>
    api.get('/orders/driver/my-orders'),

  deliverOrder: (orderId: number) =>
    api.patch(`/orders/${orderId}/driver-deliver`)
}

export const deliveryApi = {
  getNeighborhoods: (companyId: number, search?: string) =>
    api.get(`/delivery/neighborhoods/company/${companyId}`, { params: { search } }),
  
  getNeighborhoodsAdmin: (companyId: number) =>
    api.get(`/delivery/neighborhoods/company/${companyId}/admin`),
  
  getNeighborhood: (id: number) =>
    api.get(`/delivery/neighborhoods/${id}`),
  
  createNeighborhood: (data: any) =>
    api.post('/delivery/neighborhoods', data),
  
  updateNeighborhood: (id: number, data: any) =>
    api.put(`/delivery/neighborhoods/${id}`, data),
  
  deleteNeighborhood: (id: number) =>
    api.delete(`/delivery/neighborhoods/${id}`),
  
  calculateFee: (companyId: number, neighborhood: string, orderValue: number) =>
    api.get('/delivery/calculate-fee', {
      params: { company_id: companyId, neighborhood, order_value: orderValue }
    }),
}

export const paymentApi = {
  createPix: (orderId: number) =>
    api.post(`/payments/create-pix/${orderId}`),
  
  getStatus: (orderId: number) =>
    api.get(`/payments/status/${orderId}`),
  
  refund: (orderId: number, amount?: number) =>
    api.post(`/payments/refund/${orderId}`, null, { params: { amount } }),

  markAsPaid: (orderId: number) =>
    api.patch(`/payments/${orderId}/mark-paid`),
  
  getConfig: (companyId: number) =>
    api.get(`/payments/config/${companyId}`),
  
  processCard: (data: {
    order_id: number
    card_token: string
    payment_method_id: string
    installments: number
    issuer_id?: string
  }) =>
    api.post('/payments/card', data),
  
  createCheckoutPro: (orderId: number) =>
    api.post('/payments/checkout-pro', { order_id: orderId }),
  
  getOrderDetails: (orderId: number) =>
    api.get(`/payments/order/${orderId}/details`),
  
  refreshStatus: (orderId: number) =>
    api.post(`/payments/order/${orderId}/refresh-status`),
}

export const uploadApi = {
  upload: (companyId: number, formData: FormData) =>
    api.post(`/uploads/company/${companyId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
}

export const reportApi = {
  getSales: (period: string, status: string = 'delivered') =>
    api.get('/reports/sales', { params: { period, status } }),

  getDeliveries: (groupBy: string, period: string = 'month') =>
    api.get('/reports/deliveries', { params: { group_by: groupBy, period } })
}

export const businessPlanApi = {
  getAll: () =>
    api.get('/business-plans'),

  create: (data: any) =>
    api.post('/business-plans', data),

  update: (id: number, data: any) =>
    api.put(`/business-plans/${id}`, data),

  delete: (id: number) =>
    api.delete(`/business-plans/${id}`),

  getOrdersReport: (params?: any) =>
    api.get('/business-plans/report/orders', { params })
}

export default api
